import {computed, nextTick, watch} from 'vue';
import {useState} from 'nuxt/app';
import {PageData} from "~/types/response";
import {useHandleCode} from "~/composables/useHandleCode";

type FetchFunction<T> = (params: Record<string, any>) => Promise<PageData<T> | any>;

export function usePagination<T>(paginationKey: string) {
    const {handleCode, handleConstantsCodes} = useHandleCode();

    const mainPageCodeGroup = 'page';
    const commonPageSizeGroupCode = 'pageSizeGroup';
    const commonPageSizeCode = 'pageSize';

    // ✅ paginationKey를 기반으로 상태를 관리
    const currentPage = useState<number>(`${paginationKey}_currentPage`, () => 1);
    const totalPages = useState<number>(`${paginationKey}_totalPages`, () => 1);
    const pageSize = useState<number>(`${paginationKey}_pageSize`, () => 10);
    const totalElements = useState<number>(`${paginationKey}_totalElements`, () => 0);
    const fetchFunctionCache = useState<FetchFunction<T> | null>(`${paginationKey}_fetchFunctionCache`, () => null);
    const currentParams = useState<Record<string, any>>(`${paginationKey}_params`, () => ({page: 1}));
    const visiblePages = useState<number[]>(`${paginationKey}_visiblePages`, () => []);

    const pageSizeGroup = computed(() => {
        const pageSizeCodes = handleConstantsCodes(mainPageCodeGroup, commonPageSizeGroupCode) || [];

        console.log('pageSizeCodes : ', pageSizeCodes);

        /*
        로컬에서 가져오는 데이터 구조와 generate 한 데이터의 구조가 다르기에 수정


        //로컬에서 가져오는 데이터의 구조
        [
          {
            body: {
              static: "10",   // ✅ 여기에 실제 값 존재
              items: [...],
              loc: { ... },
              ...
            },
            ...
          },...
        ]

        // generate 한 데이터의 구조
        [
          {
            b: {
              s: "10",  // ✅ 운영 환경에서는 body → b, static → s 로 매핑된 듯
              i: [...],
              t: 2
            },
            ...
          },...
        ]
         */
        return pageSizeCodes.map(size => {
            const staticValue = size?.body?.static ?? size?.b?.s ?? '0'; // 기본값 설정
            return {
                text: handleCode(mainPageCodeGroup, commonPageSizeCode, staticValue) ?? '-',
                value: Number(staticValue)
            };
        });
    });

    const setParams = (params: Record<string, any>) => {
        currentParams.value = {...params, page: 0};
    };

    const setFetchFunction = (fetchFn: FetchFunction<T>) => {
        fetchFunctionCache.value = fetchFn;
    };

    const setPage = async (page: number) => {
        if (page < 1 || page > totalPages.value) return;
        currentPage.value = page;
        currentParams.value = {...currentParams.value, page: page - 1};

        if (!fetchFunctionCache.value) {
            console.error('Fetch function not set.');
            return;
        }
        const response = await fetchFunctionCache.value(currentParams.value);
        await updatePagination(response);

        await nextTick();
    };

    const updatePagination = async (data?: PageData<any> | any) => {
        if (!data) return;

        const length = data.content?.length ?? (data as any).length ?? 0;
        totalElements.value = data.totalElements ?? length;
        totalPages.value =
            data.totalPages ?? Math.ceil(totalElements.value / pageSize.value);
    };

    const updateVisiblePages = () => {
        const rangeSize = 5;
        let startPage = Math.max(1, currentPage.value - Math.floor(rangeSize / 2));
        const endPage = Math.min(totalPages.value, startPage + rangeSize - 1);
        if (endPage - startPage + 1 < rangeSize) {
            startPage = Math.max(1, endPage - rangeSize + 1);
        }
        if (totalPages.value === 2) {
            visiblePages.value = [1, 2];
        } else {
            visiblePages.value = Array.from({length: endPage - startPage + 1}, (_, i) => startPage + i);
        }
    };

    watch(totalPages, updateVisiblePages, {immediate: true});
    watch(currentPage, updateVisiblePages);

    return {
        currentPage,
        totalPages,
        pageSize,
        visiblePages,
        totalElements,
        pageSizeGroup,
        setPage,
        updatePagination,
        setParams,
        setFetchFunction,
    };
}